import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { Typography } from "@mui/material";
import { FormControlLabel } from "@material-ui/core";

import CheckboxComponent from "../common/Checkbox";
import {
  selectDisplayGradientOnGrid,
  selectIsHeatMapGradientOnGridFeatureEnabled,
  selectShowMiniHeatMap,
} from "../../store/heatmap/HeatMapSelectors";
import {
  setDisplayGradientOnGrid,
  setShowMiniHeatMap,
} from "../../store/heatmap/HeatMapActions";
import { setPrefetchPauseUntil } from "../../store/product-list/ProductListActions";
import {
  StyledCheckboxRow,
  StyledConfigSection,
} from "./HeatMapModalStyles";

/**
 * "Additional Options" section in the heat map config panel — currently
 * a single checkbox that toggles the per-tile heat map gradient bar on
 * the catalog grid. The flag persists across modal close (along with
 * the metric / colors / date range that drive the color) so the grid
 * keeps painting until the user explicitly turns it off.
 */
const HeatMapAdditionalOptions: React.FC = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const gradientChecked = useSelector(selectDisplayGradientOnGrid);
  const miniMapChecked = useSelector(selectShowMiniHeatMap);
  const isFeatureEnabled = useSelector(
    selectIsHeatMapGradientOnGridFeatureEnabled,
  );

  const handleGradientChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setPrefetchPauseUntil(Date.now() + 500));
    dispatch(setDisplayGradientOnGrid(event.target.checked));
  };

  const handleMiniMapChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setPrefetchPauseUntil(Date.now() + 500));
    dispatch(setShowMiniHeatMap(event.target.checked));
  };

  return (
    <StyledConfigSection>
      <Typography variant="heading" className="heatmap-section-label">
        {intl.formatMessage({
          id: "heatMap.additionalOptionsLabel",
          defaultMessage: "Additional Options",
        })}
      </Typography>

      {/* Mini Heat Map first — always rendered, regardless of the
        * `heat-map` feature flag. */}
      <StyledCheckboxRow>
        <FormControlLabel
          control={
            <CheckboxComponent
              checkboxId="DisplayMiniHeatMap"
              checked={miniMapChecked}
              onChange={handleMiniMapChange}
            />
          }
          label={
            <Typography variant="subtitle2">
              {intl.formatMessage({
                id: "heatMap.displayMiniHeatMap",
                defaultMessage: "Display Mini Heat Map",
              })}
            </Typography>
          }
        />
      </StyledCheckboxRow>

      {/* Gradient on Grid — gated on the `heat-map` account feature. */}
      {isFeatureEnabled && (
        <StyledCheckboxRow>
          <FormControlLabel
            control={
              <CheckboxComponent
                checkboxId="HeatMapGradientGrid"
                checked={gradientChecked}
                onChange={handleGradientChange}
              />
            }
            label={
              <Typography variant="subtitle2">
                {intl.formatMessage({
                  id: "heatMap.displayGradientOnGrid",
                  defaultMessage: "Display Heat Map Gradient on Grid",
                })}
              </Typography>
            }
          />
        </StyledCheckboxRow>
      )}
    </StyledConfigSection>
  );
};

export default HeatMapAdditionalOptions;
